import React from "react";
import { InformationCircleIcon } from "@heroicons/react/outline";

type InfoCalloutProps = {
  /** Optional bold heading rendered above the message/children (e.g. a help-panel title). */
  title?: React.ReactNode;
  /** Primary one-or-few-line message. Supports inline markup (e.g. <strong>). */
  message?: React.ReactNode;
  /** Rich body content rendered below the title/message (e.g. an explanation grid). */
  children?: React.ReactNode;
  /** Optional trailing, right-aligned slot on the first row (e.g. a "Show all" link). */
  action?: React.ReactNode;
  /**
   * Leading icon. Defaults to the info "ⓘ" icon. Pass another element to override
   * the semantics (e.g. a funnel for a filter indicator). Pass null to hide it.
   * The icon inherits its color from the callout, so omit color classes on overrides.
   */
  icon?: React.ReactNode;
  /** Extra classes for the outer container — use for positioning/margins at the call site. */
  className?: string;
};

/**
 * Static, inline informational callout — the unified blue "info" surface.
 *
 * This is the single source of truth for the blue notice look that used to be
 * hand-rolled across the app (stats filter indicators, the gaze-heatmap help
 * panel, etc.). It is intentionally NOT a toast: no auto-dismiss, no close
 * button, no absolute positioning. For transient alerts use InformationFrame.
 */
const InfoCallout = ({ title, message, children, action, icon, className }: InfoCalloutProps) => {
  const hasHeader = Boolean(title) || Boolean(message);

  return (
    <div
      className={`flex items-start gap-2 p-3 rounded-lg border border-azure-200 ${className ?? ""}`}
    >
      {icon !== null && (
        <span className="shrink-0 mt-0.5 text-azure-500">
          {icon ?? <InformationCircleIcon className="h-4 w-4" />}
        </span>
      )}

      <div className="min-w-0 flex-1">
        {title && <div className="text-sm font-semibold text-azure-800">{title}</div>}
        {message && (
          <div className={`text-sm text-azure-700 ${title ? "mt-1" : ""}`}>{message}</div>
        )}
        {children && <div className={hasHeader ? "mt-2" : ""}>{children}</div>}
      </div>

      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

export default InfoCallout;
